import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { listarTitulares } from "@/lib/cuaderno/loader";

/**
 * EL TITULAR DEL CUADERNO EN EL CHAT (M5 de la revisión de la ola 3, D14).
 *
 * El cuaderno es por titular (NIF) y año. Hasta hoy el chat mandaba solo
 * `cuaderno: { year }` y el modo cuaderno trabajaba SIEMPRE con el titular de la
 * empresa: una finca de OTRO titular (con su fila en `farm_legal_profiles`) no se
 * podía rellenar ni descargar hablando. Este selector aparece junto al chip
 * «Cuaderno de campo» SOLO si la empresa tiene más de un titular (la misma lista
 * que la hoja de /spray-logs, `listarTitulares`, y la misma caché), y lo elegido
 * viaja como `cuaderno.titular_location_id`.
 *
 * Con un solo titular no se pinta nada (el chat de siempre). Si lo elegido deja de
 * existir, vuelve al de la empresa.
 */
export function AssistantCuadernoTitular({
  companyId,
  value,
  onChange,
}: {
  companyId: string | null | undefined;
  /** null = el titular de la empresa. */
  value: string | null;
  onChange: (locationId: string | null) => void;
}) {
  const { t } = useTranslation();
  const { data: titulares } = useQuery({
    queryKey: ["cuaderno-titulares", companyId],
    enabled: !!companyId,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () => listarTitulares(supabase, companyId!),
  });

  // Lo elegido ya no es un titular (se quitó su fila): vuelve al de la empresa.
  useEffect(() => {
    if (value && titulares && !titulares.some((x) => x.locationId === value)) onChange(null);
  }, [value, titulares, onChange]);

  if (!titulares || titulares.length < 2) return null;
  return (
    <label className="inline-flex max-w-full items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="shrink-0">{t("cuadernoJunta.sheet.holder")}</span>
      <select
        data-testid="assistant-cuaderno-titular"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="min-h-[28px] max-w-[12rem] truncate rounded-full border border-[#b6dded] bg-white px-2 text-xs font-semibold text-[#1d5fa8]"
      >
        {titulares.map((x) => (
          <option key={x.locationId ?? "empresa"} value={x.locationId ?? ""}>
            {x.locationId ? x.nombre ?? t("cuadernoJunta.sheet.otherHolder") : x.nombre ?? t("cuadernoJunta.sheet.holderCompany")}
          </option>
        ))}
      </select>
    </label>
  );
}
